import { Grid, Typography } from "@mui/material";
import { useAppSelector } from "app/hooks";
import { Cell, OLTable, Row, createHeadItems } from "components/Table/OLTable";
import { OverflowChipsCell } from "components/Table/OverflowChipsCell";
import { useMessage } from "hooks/intl";
import { useCallback } from "react";
import { selectCurrentStoreData } from "reduxState/currentStoreData/currentStoreDataSlice";
import { PRODUCTS_LIST_ID } from "state/tables/types";
import { TransactionBackendModel, TransactionItem } from "types/transactions";
import { formatCurrencyValue } from "utilities/numbers";
import { KeyValuePair } from "utilities/objects";
import { useStyles } from "./ProductsSection.styles";

interface Props {
  transactionData?: TransactionBackendModel;
  openCustomAttributesModal: () => void;
  setCustomAttributes: React.Dispatch<
    React.SetStateAction<KeyValuePair[] | undefined>
  >;
}

export function ProductsSection({
  transactionData,
  openCustomAttributesModal,
  setCustomAttributes,
}: Props) {
  const classes = useStyles();
  const { message } = useMessage();
  const { currency } = useAppSelector(selectCurrentStoreData);

  const createItems = useCallback(
    (items: TransactionItem[]): Row[] => {
      let bodyItems: Row[] = items.map(
        (item, index) =>
          ({
            cells: [
              { content: item.name, property: "productName" },
              { content: item.sku, property: "productSKU" },
              { content: item.category, property: "category" },
              { content: item.maker, property: "brandName" },
              {
                content:
                  (item.labels?.length && (
                    <OverflowChipsCell
                      labels={item.labels}
                      openCustomAttributesModal={openCustomAttributesModal}
                      setCustomAttributes={setCustomAttributes}
                      data-test-id="single-transaction-product-custom-attributes"
                    />
                  )) ||
                  "-",
                property: "productCustomAttributes",
              },
              {
                align: "right",
                content: item.highPrecisionQuantity || item.quantity,
                property: "quantity",
              },
              {
                align: "right",
                content: formatCurrencyValue(item.grossValue),
                property: "productValueWithCurrency",
              },
            ] as Cell[],
            id: index,
          }) as any,
      );
      bodyItems.push({
        cells: [
          { align: "right", content: " " },
          { align: "right", content: " " },
          { align: "right", content: " " },
          { align: "right", content: " " },
          { align: "right", content: " " },
          {
            align: "right",
            content: message(
              { id: "totalTransactionValueWithCurrency" },
              { currency },
            ),
          },
          {
            align: "right",
            content: formatCurrencyValue(transactionData?.grossValue),
          },
        ],
        id: "sum",
      });
      return bodyItems;
    },
    // eslint-disable-next-line
    [classes.labelItem, currency, transactionData?.grossValue],
  );

  const items =
    transactionData?.items && transactionData?.currency
      ? createItems(transactionData.items)
      : [];

  const headItems = createHeadItems([
    {
      headerText: message({ id: "productName" }),
      property: "productName",
      isSortable: false,
    },
    {
      headerText: message({ id: "productSKU" }),
      property: "productSKU",
      isSortable: false,
    },
    {
      headerText: message({ id: "category" }),
      property: "category",
      isSortable: false,
    },
    {
      headerText: message({ id: "brandName" }),
      property: "brandName",
      isSortable: false,
    },
    {
      headerText: message({ id: "productCustomAttributes" }),
      customWidth: "1%",
      property: "productCustomAttributes",
      isSortable: false,
    },
    {
      align: "right",
      headerText: message({ id: "quantity" }),
      property: "quantity",
      isSortable: false,
    },
    {
      align: "right",
      headerText: message({ id: "productValueWithCurrency" }, { currency }),
      property: "productValueWithCurrency",
      isSortable: false,
    },
  ]);

  return (
    <Grid item xs={12}>
      <Typography className={classes.sectionTitle} marginBottom={6}>
        {message({ id: "productsInTheTransaction" })}
      </Typography>
      <OLTable
        disableColumnManager
        tableName={PRODUCTS_LIST_ID}
        className={classes.OLTable}
        columns={transactionData && headItems}
        onSort={() => {}}
        rows={transactionData && items}
        sort={{
          order: "asc",
          property: "",
          rowsPerPage: 10,
          page: 1,
        }}
        data-test-id="transaction-products"
      />
    </Grid>
  );
}
